/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useCallback, useMemo, useEffect } from 'react';
import { useQuery as useApolloQuery, useApolloClient } from '@apollo/client/react';
import { NetworkStatus } from '@apollo/client';
import { useToast } from '@crisellevaldez/speculo-ui';
import { useLocalPagination } from '../../useLocalPagination';
import { Contact } from '../../../types/contact.types';
import { ContactFormData } from '../../../schemas/contact.schema';
import { useOrganizationStore, useFlywheelEnabled } from '../../../stores/useOrganizationStore';
import { GET_CONTACTS } from '../../../lib/graphql/queries/contacts';
import type { GQLContactConsent } from './useContact';
import { createContact, deleteContact } from '../../../api/remiApi';
import type { CrmStageProjection } from '../../../types/crmStage.types';
import {
  toGraphQLFilter,
  withoutFlywheelOnlyConditions,
  type ContactAdvancedFilter,
} from '../../../types/contactFilter.types';

// Shape returned by the GraphQL API for a contact
interface GQLContact {
  contactId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  phoneStatus: string;
  emailStatus: string;
  leadSource: string;
  leadSourceSecondary: string | null;
  leadScore: number;
  leadIntent: string | null;
  recentOutcome: string;
  assignedAgentId: string | null;
  assignedAgent: {
    agentId: string;
    firstName: string;
    lastName: string;
    email: string | null;
    phone: string | null;
    photoUrl: string | null;
  } | null;
  claimedLead: string | null;
  tags: string[];
  status: string;
  lifecycleStage: string;
  optIn: boolean;
  optInDate: string | null;
  consent?: GQLContactConsent | null;
  callTester: boolean;
  lastCallAnswered: string | null;
  hasEalerts: boolean;
  flywheelOptOut: boolean;
  aiPaused: boolean;
  crmStages: CrmStageProjection[];
  flowExecutions: { flowName: string }[];
  allFlowExecutions: { flowName: string }[];
  createdAt: string | null;
}

// Response shape from GET_CONTACTS query
interface GetContactsData {
  contacts: {
    contacts?: GQLContact[];
    total?: number;
    hasMore?: boolean;
  };
}

// Map frontend sort field names to backend Spanner column names (snake_case)
const SORT_FIELD_MAP: Record<string, string> = {
  name: 'fullName',
  firstName: 'firstName',
  lastName: 'lastName',
  fullName: 'fullName',
  email: 'email',
  score: 'leadScore',
  leadScore: 'leadScore',
  engagementScore: 'engagementScore',
  readinessScore: 'readinessScore',
  contactHealth: 'contactHealth',
  recentOutcome: 'recentOutcome',
  optIn: 'optIn',
  optInDate: 'optInDate',
  opt_in: 'optIn',
  opt_in_date: 'optInDate',
  claimedLead: 'assignedAgent',
  assignedAgent: 'assignedAgent',
  lastCallAnswered: 'lastCallAnswered',
  createdAt: 'createdAt',
  updatedAt: 'updatedAt',
};

function uniqueFlowNames(executions: Array<{ flowName: string }> | null | undefined): string[] {
  const names = new Set<string>();
  for (const execution of executions ?? []) {
    const name = execution.flowName?.trim();
    if (name) names.add(name);
  }
  return Array.from(names);
}

// Map GQL field names → the Contact shape the UI already expects
function mapGQLContact(c: GQLContact): Contact & { flows: string[] } {
  return {
    id: c.contactId,
    firstName: c.firstName,
    lastName: c.lastName,
    email: c.email,
    phoneNumber: c.phone,
    phoneStatus: c.phoneStatus,
    email_status: c.emailStatus,
    leadSource: c.leadSource,
    leadSourceSecondary: c.leadSourceSecondary ?? undefined,
    score: c.leadScore,
    leadIntent: c.leadIntent ?? '',
    recentOutcome: c.recentOutcome,
    claimedLead: (() => {
      if (c.claimedLead) {
        try {
          const parsed = JSON.parse(c.claimedLead);
          if (parsed.name || parsed.agentName) {
            return {
              name: parsed.name || parsed.agentName || '',
              phoneNumber: parsed.phoneNumber ?? undefined,
              id: parsed.id || parsed.teamMemberId || undefined,
            };
          }
        } catch {
          /* fall through */
        }
      }
      return undefined;
    })(),
    assignedAgentId: c.assignedAgentId ?? null,
    assignedAgent: c.assignedAgent ?? null,
    tags: c.tags ?? [],
    status: c.status,
    lifecycleStage: c.lifecycleStage,
    opt_in: c.optIn,
    opt_in_date: c.optInDate ?? undefined,
    consent: c.consent ?? null,
    call_tester: c.callTester ?? false,
    is_test_contact: c.callTester ?? false,
    lastCallAnswered: c.lastCallAnswered ?? undefined,
    has_ealerts_registered: c.hasEalerts,
    flywheelOptOut: c.flywheelOptOut,
    aiPaused: c.aiPaused ?? false,
    crmStages: c.crmStages ?? [],
    flows: uniqueFlowNames(c.allFlowExecutions ?? c.flowExecutions),
    createdAt: c.createdAt ?? undefined,
    organization_id: undefined,
    firebaseUid: undefined,
  } as unknown as Contact & { flows: string[] };
}

export const useContacts = () => {
  const { organization } = useOrganizationStore();
  const flywheelEnabled = useFlywheelEnabled();
  const { showToast } = useToast();
  const apolloClient = useApolloClient();

  const [currentPage, setCurrentPage] = useState(0);
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [searchField, setSearchField] = useState('');
  const FILTER_DEBOUNCE_MS = 400;
  const [filters, setFilters] = useState<{
    selectedTags: string[];
    /** AND vs OR for `selectedTags` — see FilterModal. */
    matchAllTags?: boolean;
    dateRange: { startDate: Date | null; endDate: Date | null };
    outcome: string[];
    leadSource: string[];
    teamMember?: string;
    leadIntent?: string[];
    has_ealerts_registered?: boolean | null;
    flywheelOptOut?: boolean | null;
    aiPaused?: boolean | null;
    flowIds?: string[];
    flowTypes?: string[];
    voiceConsent?: string | null;
    smsConsent?: string | null;
    emailConsent?: string | null;
    /** Grouped field/operator/value conditions — see FilterModal. Compiled to
     *  SQL by the resolver, so paging and totals reflect it. */
    advancedFilter?: ContactAdvancedFilter | null;
  }>({
    selectedTags: [],
    matchAllTags: false,
    dateRange: { startDate: null, endDate: null },
    outcome: [],
    leadSource: [],
    teamMember: undefined,
    leadIntent: [],
    has_ealerts_registered: null,
    flywheelOptOut: null,
    aiPaused: null,
    flowIds: [],
    flowTypes: [],
    voiceConsent: null,
    smsConsent: null,
    emailConsent: null,
    advancedFilter: null,
  });

  /** What the query actually uses — `filters` is the live, un-debounced edit. */
  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  const {
    pageSize,
    sortField,
    sortDirection,
    updatePageSize: handlePageSizeChange,
    updateSort: handleSort,
    resetPagination,
  } = useLocalPagination({ defaultSortField: 'opt_in_date', defaultSortDirection: 'desc' });

  // Map frontend sort field to backend column name
  const backendSortBy = SORT_FIELD_MAP[sortField] || 'optInDate';

  // The builder hides Flywheel-only cards once the org loses Flywheel, but a
  // persisted condition would otherwise still be sent with nothing on screen
  // to clear it.
  const visibleAdvancedFilter = useMemo(
    () => withoutFlywheelOnlyConditions(debouncedFilters.advancedFilter, flywheelEnabled),
    [debouncedFilters.advancedFilter, flywheelEnabled]
  );

  const contactFilterVariables = {
    organizationId: organization?.id ?? '',
    search: debouncedSearch || undefined,
    dateFrom: debouncedFilters.dateRange.startDate?.toISOString() || undefined,
    dateTo: debouncedFilters.dateRange.endDate?.toISOString() || undefined,
    tags: debouncedFilters.selectedTags?.length ? debouncedFilters.selectedTags : undefined,
    matchAllTags: debouncedFilters.selectedTags?.length
      ? (debouncedFilters.matchAllTags ?? false)
      : undefined,
    flowIds: debouncedFilters.flowIds?.length ? debouncedFilters.flowIds : undefined,
    flowTypes: debouncedFilters.flowTypes?.length ? debouncedFilters.flowTypes : undefined,
    voiceConsent: debouncedFilters.voiceConsent || undefined,
    smsConsent: (flywheelEnabled && debouncedFilters.smsConsent) || undefined,
    emailConsent: debouncedFilters.emailConsent || undefined,
    recentOutcomes: debouncedFilters.outcome?.length ? debouncedFilters.outcome : undefined,
    leadSource: debouncedFilters.leadSource?.[0] || undefined,
    intentLevel: debouncedFilters.leadIntent?.[0] || undefined,
    assignedAgentId: debouncedFilters.teamMember || undefined,
    hasEalerts: debouncedFilters.has_ealerts_registered === true ? true : undefined,
    optIn: undefined,
    aiPaused: debouncedFilters.aiPaused === true ? true : undefined,
    flywheelOptOut: debouncedFilters.flywheelOptOut === false ? false : undefined,
    phoneStatus: undefined,
    advancedFilter: toGraphQLFilter(visibleAdvancedFilter),
  };

  const {
    data,
    loading: queryLoading,
    error: queryError,
    refetch: apolloRefetch,
    networkStatus,
  } = useApolloQuery<GetContactsData>(GET_CONTACTS, {
    variables: {
      ...contactFilterVariables,
      limit: pageSize,
      offset: currentPage * pageSize,
      sortBy: backendSortBy,
      sortOrder: sortDirection.toUpperCase(),
      loadContacts: true,
      loadTotal: false,
    },
    skip: !organization?.id,
    notifyOnNetworkStatusChange: true,
  });

  const {
    data: countData,
    loading: countQueryLoading,
    error: countError,
    refetch: refetchCount,
  } = useApolloQuery<GetContactsData>(GET_CONTACTS, {
    variables: {
      ...contactFilterVariables,
      // Fixed paging and sort values keep Apollo's count request stable while
      // the user pages or sorts. The backend count cache also excludes them.
      limit: 1,
      offset: 0,
      sortOrder: 'DESC',
      loadContacts: false,
      loadTotal: true,
    },
    skip: !organization?.id,
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
  });

  const rawContacts: GQLContact[] = data?.contacts?.contacts ?? [];
  const totalContacts: number | null = countData?.contacts?.total ?? null;
  const hasMore = data?.contacts?.hasMore ?? false;

  const contacts = useMemo(() => rawContacts.map((c) => mapGQLContact(c)), [rawContacts]);

  const totalPages =
    totalContacts === null ? currentPage + (hasMore ? 2 : 1) : Math.ceil(totalContacts / pageSize);
  const hasNext = totalContacts === null ? hasMore : currentPage < totalPages - 1;
  const hasPrev = currentPage > 0;
  // Only show loading skeleton on initial load or explicit refetch, not background refetches
  const isLoading = queryLoading && networkStatus !== NetworkStatus.refetch && !data;

  const handleNextPage = useCallback(() => {
    if (hasNext) setCurrentPage((p) => p + 1);
  }, [hasNext]);

  const handlePrevPage = useCallback(() => {
    if (hasPrev) setCurrentPage((p) => p - 1);
  }, [hasPrev]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const refetch = useCallback(async () => {
    setCurrentPage(0);
    setSelectedContacts([]);
    resetPagination();
    await Promise.allSettled([apolloRefetch(), refetchCount()]);
  }, [apolloRefetch, refetchCount, resetPagination]);

  useEffect(() => {
    setCurrentPage(0);
    setSelectedContacts([]);
    resetPagination();
  }, [organization?.id, resetPagination]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // The filter drawer applies on every edit, so debounce before querying —
  // otherwise typing "70" into a number condition costs two Spanner scans.
  // Mirrors the search debounce above.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedFilters(filters), FILTER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [filters]);

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      setSelectedContacts(checked ? contacts.map((c) => c.id) : []);
    },
    [contacts]
  );

  const handleSelectContact = useCallback((contactId: string, checked: boolean) => {
    setSelectedContacts((prev) =>
      checked ? [...prev, contactId] : prev.filter((id) => id !== contactId)
    );
  }, []);

  const handleSearch = useCallback(
    (value: string, field: string) => {
      setSearchField(field);
      setSearchQuery(value);
      setCurrentPage(0);
      resetPagination();
    },
    [resetPagination]
  );

  const handleDownload = useCallback(() => {
    const headers = [
      'Full Name',
      'Source',
      'Recent Outcome',
      'Last Call Answered',
      'Opt In',
      'Opt In Date',
    ];
    const csvContent = [
      headers.join(','),
      ...contacts.map((c) => {
        const optInDate = c.opt_in_date ? new Date(c.opt_in_date).toLocaleDateString() : '';
        const lastCall = c.lastCallAnswered
          ? new Date(c.lastCallAnswered).toLocaleDateString()
          : '';
        return [
          `"${c.firstName || ''} ${c.lastName || ''}"`,
          c.leadSource || '',
          c.recentOutcome || c.phoneStatus || '',
          lastCall,
          c.opt_in ? 'Yes' : 'No',
          optInDate,
        ].join(',');
      }),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'contacts.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    showToast({ message: 'Contacts downloaded successfully', variant: 'success' });
  }, [contacts, showToast]);

  const handleAddContact = useCallback(
    async (data: ContactFormData) => {
      if (!organization?.id) {
        showToast({ message: 'No organization selected', variant: 'error' });
        return false;
      }
      try {
        await createContact({ ...data, organization_id: organization.id });
        showToast({ message: 'Contact added successfully', variant: 'success' });
        refetch().catch(() => {});
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Error adding contact';
        showToast({ message, variant: 'error' });
        throw err;
      }
    },
    [showToast, refetch, organization]
  );

  const handleDeleteContact = useCallback(
    async (contact: Contact) => {
      if (!organization?.id) return;
      try {
        await deleteContact(contact.id, organization.id);
        showToast({ message: 'Contact deleted successfully', variant: 'success' });
        // Evict deleted contact from Apollo cache so it disappears immediately
        apolloClient.cache.evict({
          id: apolloClient.cache.identify({ __typename: 'Contact', contactId: contact.id }),
        });
        apolloClient.cache.gc();
        await refetch();
        setSelectedContacts([]);
      } catch {
        showToast({ message: 'Error deleting contact', variant: 'error' });
      }
    },
    [organization?.id, showToast, refetch, apolloClient]
  );

  const handleDeleteSelected = useCallback(async () => {
    if (!organization?.id) return;
    try {
      await Promise.all(selectedContacts.map((id) => deleteContact(id, organization.id)));
      showToast({
        message: `${selectedContacts.length} contact${selectedContacts.length > 1 ? 's' : ''} deleted successfully`,
        variant: 'success',
      });
      // Evict all deleted contacts from Apollo cache
      selectedContacts.forEach((id) => {
        apolloClient.cache.evict({
          id: apolloClient.cache.identify({ __typename: 'Contact', contactId: id }),
        });
      });
      apolloClient.cache.gc();
      await refetch();
      setSelectedContacts([]);
    } catch {
      showToast({ message: 'Error deleting contacts', variant: 'error' });
    }
  }, [selectedContacts, organization?.id, showToast, refetch, apolloClient]);

  const handleFilter = useCallback(
    (newFilters: typeof filters) => {
      setFilters(newFilters);
      setCurrentPage(0);
      resetPagination();
    },
    [resetPagination]
  );

  return {
    contacts,
    isLoading,
    error: queryError,
    countError,
    isCountLoading: countQueryLoading && totalContacts === null,
    totalContacts,
    resultsCount: totalContacts,
    searchQuery,
    searchField,
    selectedContacts,
    sortField,
    sortDirection,
    pageSize,
    hasNext,
    hasMore,
    hasPrev,
    currentPage,
    totalPages,
    handleSelectAll,
    handleSelectContact,
    handleSort,
    handleSearch,
    handleDownload,
    handleAddContact,
    handleDeleteContact,
    handleDeleteSelected,
    handleNextPage,
    handlePrevPage,
    handlePageChange,
    handlePageSizeChange,
    handleFilter,
    refetch,
  };
};

export default useContacts;
